import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import venueApi from '../../lib/venueApi';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { CalendarDays, Building2, Laptop, Coffee, Utensils, Notebook, User, Mail, Loader2, CheckCircle2, Sparkles, ChevronRight } from 'lucide-react';

const emptyForm = {
  from_date: '',
  to_date: '',
  company_name: '',
  location_id: '',
  lab_id: '',
  total_strength: '',
  laptops: '',
  lunch: false,
  snacks: false,
  stationery: false,
  additional_items: '',
  requester_name: '',
  requester_email: '',
  notes: '',
  status: 'prospect',
};

export default function VenueRequirementForm() {
  const [form, setForm] = useState(emptyForm);
  const [locations, setLocations] = useState([]);
  const [labs, setLabs] = useState([]);
  const [labsLoading, setLabsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    venueApi.get('/locations').then((r) => setLocations(r.data.locations || [])).catch(() => toast.error('Failed to load locations'));
  }, []);

  useEffect(() => {
    if (!form.location_id) {
      setLabs([]);
      return;
    }
    setLabsLoading(true);
    venueApi
      .get('/labs', { params: { location_id: form.location_id } })
      .then((r) => setLabs(r.data.labs || []))
      .catch(() => toast.error('Failed to load labs'))
      .finally(() => setLabsLoading(false));
  }, [form.location_id]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // ─── Slot suggestions ─────────────────────────────────────
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);

  const computeDuration = () => {
    if (form.from_date && form.to_date) {
      const days = Math.floor(
        (new Date(form.to_date) - new Date(form.from_date)) / (1000 * 60 * 60 * 24)
      ) + 1;
      return Math.max(1, days);
    }
    return 1;
  };

  const fetchSuggestions = async () => {
    if (!form.location_id) {
      toast.error('Pick a location first');
      return;
    }
    setSuggestLoading(true);
    setSuggestOpen(true);
    try {
      const params = {
        location_id: form.location_id,
        duration: computeDuration(),
        limit: 5,
        horizon_months: 6,
      };
      if (form.from_date) params.start_from = form.from_date;
      if (form.total_strength) params.min_capacity = Number(form.total_strength);
      const r = await venueApi.get('/suggest-slots', { params });
      setSuggestions(r.data.suggestions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch suggestions');
    } finally {
      setSuggestLoading(false);
    }
  };

  const applySuggestion = (s) => {
    setForm((prev) => ({
      ...prev,
      from_date: s.from_date,
      to_date: s.to_date,
      lab_id: s.lab_id,
    }));
    setSuggestOpen(false);
    toast.success(`Applied · ${s.lab_number} · ${s.from_date} → ${s.to_date}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.from_date || !form.to_date) return toast.error('Select both from and to dates');
    if (!form.company_name.trim()) return toast.error('Company name required');
    if (!form.location_id) return toast.error('Location required');
    if (!form.lab_id) return toast.error('Lab required');

    setSaving(true);
    try {
      const payload = {
        ...form,
        total_strength: Number(form.total_strength || 0),
        laptops: Number(form.laptops || 0),
      };
      const res = await venueApi.post('/requirements', payload);
      setLastCreated(res.data);
      toast.success('Requirement submitted');
      setForm(emptyForm);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="venue-form-page">
      <div>
        <h2 className="text-xl font-bold text-slate-900 tracking-tight">New Venue Requirement</h2>
        <p className="text-xs text-slate-500 mt-1">Block a lab for a prospective corporate training. Fields marked * are required.</p>
      </div>

      {lastCreated && (
        <Card className="p-4 border border-emerald-200 bg-emerald-50 flex items-start gap-3" data-testid="venue-form-success">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-emerald-900">Submitted successfully</p>
            <p className="text-[12px] text-emerald-700 mt-0.5">
              {lastCreated.company_name} · {lastCreated.lab_number} @ {lastCreated.location_name} ·
              {' '}{lastCreated.from_date} → {lastCreated.to_date}
            </p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate('/venue/calendar')} data-testid="venue-form-go-calendar">
                Open Calendar
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate('/venue/list')} data-testid="venue-form-go-list">
                See All
              </Button>
            </div>
          </div>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 border border-border lg:col-span-2 space-y-4">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <CalendarDays className="w-3.5 h-3.5 text-violet-600" /> Prospect Dates & Company
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">From Date *</Label>
              <Input type="date" value={form.from_date} onChange={(e) => set('from_date', e.target.value)} data-testid="venue-from-date" />
            </div>
            <div>
              <Label className="text-xs">To Date *</Label>
              <Input type="date" value={form.to_date} onChange={(e) => set('to_date', e.target.value)} data-testid="venue-to-date" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Company Name *</Label>
            <Input
              value={form.company_name}
              onChange={(e) => set('company_name', e.target.value)}
              placeholder="e.g. Acme Corp"
              data-testid="venue-company-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Location *</Label>
              <Select value={form.location_id} onValueChange={(v) => { set('location_id', v); set('lab_id', ''); }}>
                <SelectTrigger data-testid="venue-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Lab Number *</Label>
              <Select value={form.lab_id} onValueChange={(v) => set('lab_id', v)} disabled={!form.location_id || labsLoading}>
                <SelectTrigger data-testid="venue-lab">
                  <SelectValue placeholder={!form.location_id ? 'Select location first' : (labsLoading ? 'Loading…' : (labs.length === 0 ? 'No labs configured' : 'Select lab'))} />
                </SelectTrigger>
                <SelectContent>
                  {labs.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.lab_number}{l.capacity ? ` · cap ${l.capacity}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ─── Slot Suggestion Panel ─── */}
          {form.location_id && (
            <div className="rounded-lg border border-violet-100 bg-gradient-to-br from-violet-50/60 to-indigo-50/40" data-testid="venue-suggestions-panel">
              <button
                type="button"
                onClick={() => (suggestOpen ? setSuggestOpen(false) : fetchSuggestions())}
                data-testid="venue-suggest-btn"
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-violet-50/50 rounded-lg transition"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-600" />
                  <div>
                    <p className="text-[13px] font-semibold text-slate-800">Find best available slot</p>
                    <p className="text-[11px] text-slate-500">
                      {form.from_date && form.to_date
                        ? `Suggest labs free for ${computeDuration()} day(s) from ${form.from_date}`
                        : (form.total_strength
                          ? `Suggest labs with capacity ≥ ${form.total_strength}`
                          : 'Suggest earliest labs available in the next 6 months')}
                    </p>
                  </div>
                </div>
                <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${suggestOpen ? 'rotate-90' : ''}`} />
              </button>

              {suggestOpen && (
                <div className="border-t border-violet-100 p-3 space-y-2" data-testid="venue-suggestions-list">
                  {suggestLoading ? (
                    <div className="flex items-center justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-violet-600" /></div>
                  ) : suggestions.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-xs text-slate-500">No matching slots found in the next 6 months.</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">Try reducing the strength or picking a different location.</p>
                    </div>
                  ) : (
                    suggestions.map((s, i) => {
                      const capOk = !form.total_strength || s.capacity >= Number(form.total_strength);
                      const isPickedDate = form.from_date === s.from_date;
                      return (
                        <button
                          key={`${s.lab_id}-${s.from_date}`}
                          type="button"
                          onClick={() => applySuggestion(s)}
                          data-testid={`venue-suggestion-${i}`}
                          className="w-full flex items-center gap-3 bg-white hover:bg-violet-50 border border-slate-200 hover:border-violet-300 rounded-md p-2.5 text-left transition"
                        >
                          <div className="flex-shrink-0 w-9 h-9 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center text-[11px] font-bold">
                            {s.distance_days === 0 ? 'NOW' : `+${s.distance_days}d`}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-800">
                              {s.from_date}{s.duration_days > 1 ? ` → ${s.to_date}` : ''}
                              {isPickedDate && <span className="ml-2 text-[10px] font-normal text-violet-600">(matches your dates)</span>}
                            </p>
                            <p className="text-[11px] text-slate-500 flex items-center gap-2">
                              <Building2 className="w-3 h-3" /> {s.lab_number} · {s.location_name}
                              {s.capacity ? (
                                <span className={capOk ? 'text-emerald-700' : 'text-amber-700'}>
                                  · cap {s.capacity}
                                  {!capOk && ' (below strength)'}
                                </span>
                              ) : null}
                            </p>
                          </div>
                          <span className="text-[11px] text-violet-600 font-medium">Apply →</span>
                        </button>
                      );
                    })
                  )}
                  <div className="flex justify-end pt-1">
                    <Button type="button" size="sm" variant="ghost" className="h-7 text-[11px]" onClick={fetchSuggestions} data-testid="venue-suggest-refresh">
                      <Loader2 className={`w-3 h-3 mr-1 ${suggestLoading ? 'animate-spin' : 'hidden'}`} />
                      Refresh
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Total Strength</Label>
              <Input type="number" min="0" value={form.total_strength} onChange={(e) => set('total_strength', e.target.value)} placeholder="e.g. 30" data-testid="venue-strength" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Laptop className="w-3 h-3" /> Laptops Needed</Label>
              <Input type="number" min="0" value={form.laptops} onChange={(e) => set('laptops', e.target.value)} placeholder="e.g. 25" data-testid="venue-laptops" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Additional Items / Notes</Label>
            <Textarea
              value={form.additional_items}
              onChange={(e) => set('additional_items', e.target.value)}
              placeholder="e.g. projector, whiteboard, extra chairs…"
              rows={2}
              data-testid="venue-additional"
            />
          </div>
        </Card>

        <Card className="p-5 border border-border space-y-4">
          <h3 className="text-sm font-semibold text-slate-800">Support Requirements</h3>

          <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-slate-50" data-testid="venue-lunch-row">
            <Checkbox checked={form.lunch} onCheckedChange={(v) => set('lunch', !!v)} data-testid="venue-lunch" />
            <Utensils className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-sm">Lunch required</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-slate-50" data-testid="venue-snacks-row">
            <Checkbox checked={form.snacks} onCheckedChange={(v) => set('snacks', !!v)} data-testid="venue-snacks" />
            <Coffee className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-sm">Snacks required</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-slate-50" data-testid="venue-stationery-row">
            <Checkbox checked={form.stationery} onCheckedChange={(v) => set('stationery', !!v)} data-testid="venue-stationery" />
            <Notebook className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-sm">Stationery required</span>
          </label>

          <div className="pt-2 border-t border-slate-100">
            <Label className="text-xs">Status</Label>
            <div className="flex gap-2 mt-1.5">
              {[
                { v: 'prospect', label: 'Prospect', cls: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
                { v: 'confirmed', label: 'Confirmed', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
              ].map((s) => (
                <button
                  key={s.v}
                  type="button"
                  onClick={() => set('status', s.v)}
                  data-testid={`venue-status-${s.v}`}
                  className={`text-xs px-3 py-1.5 rounded-full border font-medium transition ${
                    form.status === s.v ? s.cls : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-2 border-t border-slate-100 space-y-3">
            <p className="text-[11px] uppercase text-slate-400 tracking-wide">Requester (optional)</p>
            <div>
              <Label className="text-xs flex items-center gap-1"><User className="w-3 h-3" /> Your Name</Label>
              <Input value={form.requester_name} onChange={(e) => set('requester_name', e.target.value)} placeholder="Name" data-testid="venue-req-name" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Mail className="w-3 h-3" /> Your Email</Label>
              <Input type="email" value={form.requester_email} onChange={(e) => set('requester_email', e.target.value)} placeholder="you@company.com" data-testid="venue-req-email" />
            </div>
          </div>
        </Card>

        <div className="lg:col-span-3 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => setForm(emptyForm)} className="rounded-md" data-testid="venue-form-reset">
            Reset
          </Button>
          <Button type="submit" disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white rounded-md min-w-[140px]" data-testid="venue-form-submit">
            {saving ? (<><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Submitting…</>) : 'Submit Requirement'}
          </Button>
        </div>
      </form>
    </div>
  );
}
